import { Table, Button, Tag, Popconfirm, message, Space, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import {
  AppFilter,
  AppFilterButton,
  AppProcessEarningsFilter,
  DEFAULT_PAYOUT_STATUS,
} from "components/AppFilter";
import { AppPageHeader } from "components/AppPageHeader";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import styled from "styled-components";
import { IGigWorkerPayout } from "store/partner-services/partnerServicesSlice";

const MESSAGE_KEY = "process_earnings";

const STATUS_COLOR: Record<string, string> = {
  AWAITING_APPROVAL: "orange",
  PROCESSING: "blue",
  SUCCESS: "green",
  FAILED: "red",
};

const PUSH_STATUSES = new Set(["AWAITING_APPROVAL"]);
const DISCARD_STATUSES = new Set(["AWAITING_APPROVAL", "PROCESSING"]);
const SUCCESS_STATUSES = new Set(["PROCESSING"]);

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Controls = styled.div`
  flex-shrink: 0;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 0;
`;

const ActionBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
`;

const ProcessEarningsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Process Earnings");
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    payouts,
    payoutsStatus,
    payoutsError,
    getPayoutsThunk,
    processPayoutsThunk,
    markSuccessPayoutsThunk,
  } = config;

  const [searchParams, setSearchParams] = useSearchParams();
  const [filterState, setFilterState] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const month = searchParams.get("month") ?? "";
  const status = searchParams.get("status") ?? "";

  useEffect(() => {
    if (!month || !status) {
      setSearchParams(
        {
          month: month || moment().format("YYYY-MM"),
          status: status || DEFAULT_PAYOUT_STATUS,
        },
        { replace: true }
      );
    }
  }, [month, status, setSearchParams]);

  // getPayoutsThunk and dispatch are stable module-level refs — safe as deps.
  const getData = useCallback(() => {
    if (!month || !status) return;
    dispatch(getPayoutsThunk({ month, status }));
  }, [month, status, dispatch, getPayoutsThunk]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [payouts]);

  useEffect(() => {
    if (payoutsStatus === "error" && payoutsError) {
      message.error({ content: payoutsError, key: MESSAGE_KEY });
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [payoutsStatus, payoutsError]);

  const isProcessing = payoutsStatus === "processing";
  const isLoading = payoutsStatus === "loading";

  const payoutStatusMap = useMemo<Record<number, string>>(
    () => Object.fromEntries(payouts.map((p) => [p.payoutId, p.status])),
    [payouts]
  );

  const pushIds = useMemo(
    () => selectedRowKeys.filter((id) => PUSH_STATUSES.has(payoutStatusMap[id])),
    [selectedRowKeys, payoutStatusMap]
  );
  const discardIds = useMemo(
    () => selectedRowKeys.filter((id) => DISCARD_STATUSES.has(payoutStatusMap[id])),
    [selectedRowKeys, payoutStatusMap]
  );
  const successIds = useMemo(
    () => selectedRowKeys.filter((id) => SUCCESS_STATUSES.has(payoutStatusMap[id])),
    [selectedRowKeys, payoutStatusMap]
  );

  const selectedCount = selectedRowKeys.length;
  const hasSelection = selectedCount > 0;

  const handleProcess = async (action: "PUSH" | "DISCARD") => {
    if (!month) return;
    const ids = action === "PUSH" ? pushIds : discardIds;
    const actionLabel = action === "PUSH" ? "Processing" : "Discarding";
    message.loading({ content: `${actionLabel} payouts...`, key: MESSAGE_KEY });
    const result = await dispatch(processPayoutsThunk({ month, action, payoutIds: ids }));
    if (processPayoutsThunk.rejected.match(result)) {
      const errMsg = (result.payload as { message?: string })?.message;
      message.error({ content: errMsg ?? "Action failed!", key: MESSAGE_KEY });
    } else {
      const doneLabel = action === "PUSH" ? "processed" : "discarded";
      message.success({
        content: `${ids.length} payout(s) ${doneLabel} successfully.`,
        key: MESSAGE_KEY,
      });
      getData();
    }
  };

  const handleMarkSuccess = async () => {
    if (!month) return;
    message.loading({ content: "Marking payouts as success...", key: MESSAGE_KEY });
    const result = await dispatch(markSuccessPayoutsThunk({ month, payoutIds: successIds }));
    if (markSuccessPayoutsThunk.rejected.match(result)) {
      const errMsg = (result.payload as { message?: string })?.message;
      message.error({ content: errMsg ?? "Action failed!", key: MESSAGE_KEY });
    } else {
      message.success({
        content: `${successIds.length} payout(s) marked as success.`,
        key: MESSAGE_KEY,
      });
      getData();
    }
  };

  const filterCount = useMemo(
    () => ["month", "status"].filter((k) => searchParams.get(k)).length,
    [searchParams]
  );

  const columns: ColumnsType<IGigWorkerPayout> = [
    { key: "payoutId", title: "Payout ID", dataIndex: "payoutId", width: 90 },
    { key: "userId", title: "User ID", dataIndex: "userId", width: 80 },
    {
      key: "mysaPartnerId",
      title: "MYSA Partner ID",
      dataIndex: "mysaPartnerId",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    { key: "mysaExternalId", title: "External ID", dataIndex: "mysaExternalId" },
    { key: "payoutMonth", title: "Payout Month", dataIndex: "payoutMonth", width: 110 },
    {
      key: "totalAmount",
      title: "Total Amount (₹)",
      dataIndex: "totalAmount",
      render: (val: number) => <>{val.toFixed(2)}</>,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (val: string) => (
        <Tag color={STATUS_COLOR[val] ?? "default"}>{val.replace(/_/g, " ")}</Tag>
      ),
    },
    {
      key: "mysaRaisedOn",
      title: "Raised On",
      dataIndex: "mysaRaisedOn",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as number[]),
  };

  return (
    <PageWrapper>
      <Controls>
        <AppPageHeader title="Process Earnings" />
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState((s) => !s)}
              filterCount={filterCount}
            />
          </div>
        </AppActionStrip>
        <AppFilter expanded={filterState}>
          <AppProcessEarningsFilter />
        </AppFilter>
      </Controls>

      <TableWrapper>
        <Table<IGigWorkerPayout>
          columns={columns}
          dataSource={payouts}
          rowKey="payoutId"
          rowSelection={rowSelection}
          scroll={{ x: "max-content" }}
          size="small"
          pagination={false}
          loading={isLoading}
          bordered
          sticky
        />
      </TableWrapper>

      <ActionBar>
        {hasSelection && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {selectedCount} selected
          </Typography.Text>
        )}
        <Space>
          <Popconfirm
            title={`Push ${pushIds.length} payout(s) for processing?`}
            onConfirm={() => handleProcess("PUSH")}
            okText="Process"
            cancelText="Cancel"
            disabled={pushIds.length === 0 || isProcessing}
          >
            <Button
              type="primary"
              disabled={pushIds.length === 0 || isProcessing}
              loading={isProcessing}
            >
              Process ({pushIds.length})
            </Button>
          </Popconfirm>

          <Popconfirm
            title={`Discard ${discardIds.length} payout(s)? This cannot be undone.`}
            onConfirm={() => handleProcess("DISCARD")}
            okText="Discard"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            disabled={discardIds.length === 0 || isProcessing}
          >
            <Button
              danger
              disabled={discardIds.length === 0 || isProcessing}
              loading={isProcessing}
            >
              Discard ({discardIds.length})
            </Button>
          </Popconfirm>

          <Popconfirm
            title={`Mark ${successIds.length} payout(s) as Success?`}
            onConfirm={handleMarkSuccess}
            okText="Mark Success"
            cancelText="Cancel"
            disabled={successIds.length === 0 || isProcessing}
          >
            <Button
              disabled={successIds.length === 0 || isProcessing}
              loading={isProcessing}
            >
              Mark Success ({successIds.length})
            </Button>
          </Popconfirm>
        </Space>
      </ActionBar>
    </PageWrapper>
  );
};

export default ProcessEarningsPage;
